import React, {useState, useEffect, useMemo} from 'react';
import {useMutation, useQuery} from '@apollo/client';

import {Box, Button, IconButton, Stack} from '@mui/material';
import {DataGridPro} from '@mui/x-data-grid-pro';
import {Delete, Edit} from '@mui/icons-material';

import {DELETE_TAG, FETCH_INTERNAL_TAGS} from '../queries/GraphQLQueries';
import {TAG_COLORS} from '../helpers/MemberTagHelper';
import {SENTIMENT} from "../helpers/SessionHelper";
import {TAG_SCOPE, groupMasterTagsByCategory, buildHierarchicalTagRows} from '../helpers/TagHelper';
import {useDataGridExpansionState} from '../hooks/useDataGridExpansionState';

import AddEditTagDialog from '../components/tag-manager/AddEditTagDialog';
import ConfirmationDialog from '../components/common/ConfirmationDialog';
import TagManagerTabs from '../components/tag-manager/TagManagerTabs';

const styles = {
    container: {
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
        paddingTop: '0',
        width: '100%',
        height: 'calc(100vh - 16px)',
    },
    colorIndicator: {
        borderRadius: '50%',
        height: '1rem',
        width: '1rem',
        display: 'inline-block',
    },
    addButton: {
        padding: '0.25rem 0.75rem',
    },
    dataGrid: {
        '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
        },
    },
    headerStack: {
        mb: '0.5rem',
        alignItems: 'center',
    },
};

const defaultRowHeightInPixels = 40;

export default function TagManagerView() {
    const [tags, setTags] = useState({});
    const [rows, setRows] = useState([]);
    const [scope, setScope] = useState(TAG_SCOPE.INTERNAL);
    const [isAddTagDialogOpen, setIsAddTagDialogOpen] = useState(false);
    const [selectedTag, setSelectedTag] = useState(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [tagToDelete, setTagToDelete] = useState(null);

    const {apiRef, isGroupExpandedByDefault} = useDataGridExpansionState(rows);

    const {data: masterTagsData} = useQuery(FETCH_INTERNAL_TAGS);

    const [deleteTag] = useMutation(DELETE_TAG, {
        refetchQueries: [{query: FETCH_INTERNAL_TAGS}],
    });

    const handleScopeChange = (event, newValue) => setScope(newValue);
    const handleOpenAddTagDialog = () => {
        setSelectedTag(null);
        setIsAddTagDialogOpen(true);
    };
    const handleCloseAddTagDialog = () => {
        setSelectedTag(null);
        setIsAddTagDialogOpen(false);
    };

    const handleEditTag = (tag) => {
        setSelectedTag(tag);
        setIsAddTagDialogOpen(true);
    };

    const handleDeleteTagClick = (tag) => {
        setTagToDelete(tag);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (tagToDelete) {
            try {
                await deleteTag({
                    variables: {
                        tagId: tagToDelete.id
                    }
                });
                setIsDeleteDialogOpen(false);
                setTagToDelete(null);
            } catch (err) {
                console.error('Failed to delete tag:', err);
            }
        }
    };

    const handleCancelDelete = () => {
        setIsDeleteDialogOpen(false);
        setTagToDelete(null);
    };

    useEffect(() => {
        if (masterTagsData?.tags) {
            const groupedTags = groupMasterTagsByCategory(masterTagsData.tags);
            setTags(groupedTags);
        }
    }, [masterTagsData]);

    const columns = useMemo(() => {
        const commonColumnProperties = {
            width: 240,
            editable: false,
            sortable: true,
            headerAlign: 'start',
            align: 'start',
        };

        return [
            {
                field: "color",
                headerName: "Tag Type",
                renderCell: (params) => {
                    if (params.row.isCategory) {
                        return null;
                    }
                    const color = params.row.sentiment === SENTIMENT.POSITIVE ? TAG_COLORS.POSITIVE : TAG_COLORS.NEGATIVE;
                    return <Box sx={{...styles.colorIndicator, backgroundColor: color}} />;
                },
                ...commonColumnProperties,
                width: 120,
            },
            {
                field: "label",
                headerName: "Tag Name",
                renderCell: (params) => {
                    if (params.row.isCategory) {
                        return null;
                    }
                    return params.row.label;
                },
                ...commonColumnProperties,
                width: 180,
            },
            {
                field: "actions",
                headerName: "Actions",
                renderCell: (params) => {
                    if (params.row.isCategory) {
                        return null;
                    }
                    return (
                        <Stack direction="row" spacing={1}>
                            <IconButton
                                color="default"
                                size="small"
                                aria-label="edit"
                                onClick={() => handleEditTag(params.row)}
                            >
                                <Edit fontSize="small"/>
                            </IconButton>
                            <IconButton
                                color="default"
                                size="small"
                                aria-label="delete"
                                onClick={() => handleDeleteTagClick(params.row)}
                            >
                                <Delete fontSize="small"/>
                            </IconButton>
                        </Stack>
                    );
                },
                ...commonColumnProperties,
                sortable: false,
                width: 150,
            }
        ];
    }, []);

    useEffect(() => {
        setRows(buildHierarchicalTagRows(tags));
    }, [tags]);

    return (
        <Stack sx={styles.container}>
            <Stack spacing={2} direction="row" sx={styles.headerStack}>
                <Button
                    variant="contained"
                    size="small"
                    sx={styles.addButton}
                    onClick={handleOpenAddTagDialog}>
                    Add Tag
                </Button>
                <TagManagerTabs scope={scope} onScopeChange={handleScopeChange} />
            </Stack>

            <AddEditTagDialog
                open={isAddTagDialogOpen}
                onClose={handleCloseAddTagDialog}
                tag={selectedTag}/>

            <ConfirmationDialog
                open={isDeleteDialogOpen}
                title="Delete Tag"
                message={tagToDelete ? `Are you sure you want to delete the tag "${tagToDelete.label}"?`: ''}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}/>

            {
              scope === TAG_SCOPE.INTERNAL
              &&
              (<DataGridPro
                  treeData
                  sx={styles.dataGrid}
                  rowHeight={defaultRowHeightInPixels}
                  hideFooter={true}
                  disableMultipleColumnsSorting={true}
                  apiRef={apiRef}
                  rows={rows}
                  columns={columns}
                  getTreeDataPath={(row) => row.hierarchy}
                  groupingColDef={{
                      headerName: "Tag Category",
                      width: 215,
                      valueGetter: (value, row) => {
                          if (row?.isCategory) {
                              return row.category;
                          }
                          return '';
                      }
                  }}
                  defaultGroupingExpansionDepth={0}
                  isGroupExpandedByDefault={isGroupExpandedByDefault}/>
              )}
        </Stack>
    );
}